import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  OnInit,
  output,
  OutputEmitterRef,
  signal,
  WritableSignal
} from '@angular/core';
import {MatFormField, MatInput} from "@angular/material/input";
import {MatOption} from "@angular/material/core";
import {MatRadioButton, MatRadioGroup} from "@angular/material/radio";
import {MatSelect} from "@angular/material/select";
import {TranslatePipe} from "@ngx-translate/core";
import {EditObjectDialogData} from "../../edit-object-dialog-data";
import {SelectObjectType} from "../../edit-object-dialog/edit-object-dialog.component";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {CmsApiService} from "../../../core/cms-api.service";
import {IdentifierFormat} from "../../../administration/admin-id-format/identifier-format";

@Component({
  selector: 'app-consists-of-step1',
  imports: [
    MatFormField,
    MatOption,
    MatRadioButton,
    MatRadioGroup,
    MatSelect,
    TranslatePipe,
    MatInput,
    ReactiveFormsModule,
    FormsModule
  ],
  templateUrl: './consists-of-step1.html',
  styleUrl: './consists-of-step1.scss',
})
export class ConsistsOfStep1 implements OnInit {
  private readonly cmsApi: CmsApiService = inject(CmsApiService);

  data: InputSignal<EditObjectDialogData> = input();
  selectObjectTypes: InputSignal<SelectObjectType[]> = input();

  actionSelected: OutputEmitterRef<string> = output();
  objectTypeSelected: OutputEmitterRef<SelectObjectType> = output();
  targetCountSelected: OutputEmitterRef<number> = output();

  identifierFormats: WritableSignal<IdentifierFormat[]> = signal([]);
  selectedAction: WritableSignal<string> = signal('new_object');
  selectedObjectType: WritableSignal<string> = signal('');
  numberOfCopies: WritableSignal<number> = signal(1);

  rootObject = computed(() => this.data().fieldParameters.sectionsContainer.rootObject);
  rootObjectType = computed<string>(() => this.rootObject().object_type);

  identifierFormat = computed<IdentifierFormat | undefined>(() => {
    const superobjectTypeId = this.rootObject().superobject_type_id;
    const collectionId = this.rootObject().collection?.collection_id;
    let format = this.identifierFormats().find(f =>
      f.identifier_format_superobject_type_id === superobjectTypeId &&
      f.identifier_format_collection_id === collectionId
    );
    if (!format) {
      format = this.identifierFormats().find(f =>
        f.identifier_format_superobject_type_id === superobjectTypeId &&
        f.identifier_format_collection_id === null
      );
    }
    return format;
  });

  maxCopies = computed<number>(() => {
    if (this.identifierFormat()) {
      const subTypeDigits = this.identifierFormat()?.subtype_digits;
      if (subTypeDigits) {
        const maxCopies = Math.pow(10, subTypeDigits) - 1;
        return maxCopies > 0 && maxCopies <= 100 ? maxCopies : 99;
      }
    }
    return 99;
  });

  ngOnInit() {
    this.cmsApi.getAllIdentifierFormat().then((formats) => {
      this.identifierFormats.set(formats);
    }).catch(() => {
      console.error('Failed to load identifier formats');
    });

    setTimeout(() => {
      // Emit initial values after the initial change detection cycle
      this.selectTargetCount();
      this.selectObjectType(this.findObjectTypeByValue(this.rootObjectType()));
    });
  }

  selectAction(action: string) {
    if (action !== 'new_object') {
      for (const objectType of this.selectObjectTypes()) {
        if (objectType.name) {
          if (objectType.objectType === this.rootObjectType()) {
            this.selectObjectType(objectType);
          }
        }
      }
    }

    this.selectedAction.set(action);
    this.actionSelected.emit(action);
  }

  selectObjectType(objectType: SelectObjectType) {
    this.selectedObjectType.set(objectType.objectType);
    this.objectTypeSelected.emit(objectType);
  }

  selectTargetCount() {
    const maxCopies = this.maxCopies();
    const copies = this.numberOfCopies();
    this.targetCountSelected.emit(copies >= 1 && copies <= maxCopies ? copies : null);
  }

  findObjectTypeByValue(value: string): SelectObjectType | undefined {
    return this.selectObjectTypes().find(objectType => objectType.objectType === value);
  }
}
